import React from 'react';
import { cn } from '../../../utils';
import type { Position, ConnectionState } from '../../../types';
import { PhoneIcon, LoaderIcon, BotIcon } from '../../shared/icons';

export interface MinimalFloatingButtonProps {
  /** Click handler */
  onClick: () => void;
  /** Widget position */
  position?: Position;
  /** Avatar image URL */
  avatar?: string;
  /** Agent name */
  agentName?: string;
  /** Enable pulse animation */
  pulse?: boolean;
  /** Connection state */
  status?: ConnectionState;
  /** Custom offset */
  offset?: { x?: number; y?: number };
  /** Additional class names */
  className?: string;
  /** Hide when needed */
  isHidden?: boolean;
}

/**
 * Minimal style floating button - Minimal-style with a card
 */
export function MinimalFloatingButton({
  onClick,
  position = 'bottom-left',
  avatar,
  agentName = 'AI Assistant',
  status = 'idle',
  offset,
  className,
  isHidden,
}: MinimalFloatingButtonProps) {
  if (isHidden) return null;

  const isLoading = status === 'connecting' || status === 'reconnecting';

  const positionStyles: React.CSSProperties = {
    position: 'fixed',
    bottom: offset?.y ?? 24,
    ...(position === 'bottom-left' ? { left: offset?.x ?? 24 } : { right: offset?.x ?? 24 }),
    zIndex: 'var(--qcall-z-widget)',
  };

  return (
    <div
      className={cn(
        // Base styles - card container
        'bg-[#323232] rounded-2xl p-4 w-[240px]',
        'shadow-[0_8px_30px_rgb(0,0,0,0.4)]',
        'border border-[#444]',
        'animate-in fade-in slide-in-from-bottom-4 duration-300',
        className
      )}
      style={positionStyles}
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 flex items-center justify-center shrink-0 overflow-hidden rounded-full">
          {avatar ? (
            <img src={avatar} alt={agentName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-indigo-500 flex items-center justify-center">
              <BotIcon className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
        <span className="text-white text-sm font-medium tracking-wide">{agentName}</span>
      </div>

      <button
        type="button"
        onClick={onClick}
        className={cn(
          'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl',
          'bg-[#165a50] hover:bg-[#124d44] text-white',
          'transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#165a50] focus:ring-offset-2 focus:ring-offset-[#323232]'
        )}
        aria-label="Start conversation"
      >
        {isLoading ? (
          <LoaderIcon className="w-4 h-4 text-white animate-spin" />
        ) : (
          <PhoneIcon className="w-4 h-4" />
        )}
        <span className="font-medium text-sm">Talk to {agentName}</span>
      </button>
    </div>
  );
}
